import { Box, CircularProgress } from '@mui/material';
import React, { lazy, Suspense } from 'react';
import { Navigate, Outlet, Route, Routes } from 'react-router';

import useSession from '../hooks/useSession';
import {
  Overview as PrototypeOverview,
  PlaceholderPage,
  PrototypeActions,
  PrototypeFAQs,
  PrototypeLayout,
} from '../pages/Prototype';
import Callback from '../utils/Callback';
import Login from '../utils/Login';
import Unauthorized from '../utils/Unauthorized';

// Lazy load PrototypeV2 components
const PrototypeV2Layout = lazy(() => import('../pages/PrototypeV2/Layout'));
const PrototypeV2FAQs = lazy(() => import('../pages/PrototypeV2/FAQs'));
const PrototypeV2Actions = lazy(() => import('../pages/PrototypeV2/Actions'));

// Lazy load components that use Tableau or other heavy dependencies
const ManageActions = lazy(() => import('../pages/Actions/ManageActions'));
const StoryblokPreview = lazy(() => import('../pages/Storyblok/StoryblokPreview'));
const StoryblokActionPreview = lazy(() => import('../pages/Storyblok/StoryblokActionPreview'));
const Analytics = lazy(() => import('../pages/Analytics/Analytics'));
const ReportRouter = lazy(() => import('../pages/Analytics/ReportRouter'));
const FAQs = lazy(() => import('../pages/Content/FAQs'));
const Cards = lazy(() => import('../pages/Content/Cards'));
const Agreements = lazy(() => import('../pages/Content/Agreements'));
const Layout = lazy(() => import('../pages/Layouts/Layout'));
const Members = lazy(() => import('../pages/Members/Members'));
const ImportTenant = lazy(() => import('../pages/Tenant/ImportTenant'));
const TenantConfig = lazy(() => import('../pages/Tenant/TenantConfig'));
const TenantDetails = lazy(() => import('../pages/Tenant/TenantDetails'));
const TenantInfoRedirect = lazy(() => import('../pages/Tenant/TenantInfoRedirect'));

const FullScreenLoader = () => (
  <Box
    sx={{
      height: '100vh',
      width: '100vw',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
    }}
  >
    <CircularProgress />
  </Box>
);

export default function AppRoutes() {
  const { isLoggedIn, isLoading } = useSession();

  const Protected = () => {
    if (isLoading) return <FullScreenLoader />;
    return isLoggedIn ? <Outlet /> : <Navigate to="/" replace />;
  };

  const UnProtected = () => {
    if (isLoading) return <FullScreenLoader />;
    return isLoggedIn ? <Navigate to="/analytics" replace /> : <Outlet />;
  };

  return (
    <Routes>
      {/* Prototype routes - accessible without auth for development */}
      <Route path="/prototype" element={<PrototypeLayout />}>
        <Route index element={<Navigate to="analytics/overview" replace />} />
        <Route path="analytics/overview" element={<PrototypeOverview />} />
        <Route path="analytics/activation" element={<PlaceholderPage />} />
        <Route path="analytics/actions" element={<PlaceholderPage />} />
        <Route path="analytics/cards" element={<PlaceholderPage />} />
        <Route path="analytics/members" element={<PlaceholderPage />} />
        <Route path="content/cards" element={<PlaceholderPage />} />
        <Route path="content/faqs" element={<PrototypeFAQs />} />
        <Route path="content/agreements" element={<PlaceholderPage />} />
        <Route path="actions" element={<PrototypeActions />} />
        <Route path="questionnaire" element={<PlaceholderPage />} />
        <Route path="cohorts" element={<PlaceholderPage />} />
        <Route path="customizations" element={<PlaceholderPage />} />
        <Route path="tenant-settings" element={<PlaceholderPage />} />
      </Route>

      {/* PrototypeV2 routes - New design from Figma */}
      <Route
        path="/prototypev2"
        element={
          <Suspense fallback={<FullScreenLoader />}>
            <PrototypeV2Layout />
          </Suspense>
        }
      >
        <Route index element={<Navigate to="content/faqs" replace />} />
        <Route
          path="content/faqs"
          element={
            <Suspense fallback={<FullScreenLoader />}>
              <PrototypeV2FAQs />
            </Suspense>
          }
        />
        <Route path="content/cards" element={<PlaceholderPage />} />
        <Route path="content/agreements" element={<PlaceholderPage />} />
        <Route path="analytics/overview" element={<PlaceholderPage />} />
        <Route path="analytics/reports" element={<PlaceholderPage />} />
        <Route
          path="actions"
          element={
            <Suspense fallback={<FullScreenLoader />}>
              <PrototypeV2Actions />
            </Suspense>
          }
        />
        <Route path="questionnaire" element={<PlaceholderPage />} />
        <Route path="cohorts" element={<PlaceholderPage />} />
      </Route>

      {/* Storyblok content preview - public */}
      <Route
        path="/storyblok/faq"
        element={
          <Suspense fallback={<FullScreenLoader />}>
            <StoryblokPreview />
          </Suspense>
        }
      />
      <Route
        path="/storyblok/action/stepitup"
        element={
          <Suspense fallback={<FullScreenLoader />}>
            <StoryblokActionPreview storySlug="kpcustomer/kpsponsor/kp/action/step-it-up/root-step-it-up-page" />
          </Suspense>
        }
      />
      <Route
        path="/storyblok/action/eatwell"
        element={
          <Suspense fallback={<FullScreenLoader />}>
            <StoryblokActionPreview storySlug="kpcustomer/kpsponsor/kp/action/eat-well-to-fuel-your-body/eat-well-to-page" />
          </Suspense>
        }
      />

      {/* Public / auth pages */}
      <Route element={<UnProtected />}>
        <Route path="/" element={<Login />} />
        <Route path="/callback" element={<Callback />} />
      </Route>

      {/* Protected app - wrapped in Suspense for lazy loading */}
      <Route element={<Protected />}>
        <Route
          path="/unauthorized"
          element={
            <Suspense fallback={<FullScreenLoader />}>
              <Layout />
            </Suspense>
          }
        >
          <Route index element={<Unauthorized />} />
        </Route>
        <Route
          path="/analytics/*"
          element={
            <Suspense fallback={<FullScreenLoader />}>
              <Layout />
            </Suspense>
          }
        >
          <Route
            index
            element={
              <Suspense fallback={<FullScreenLoader />}>
                <Analytics />
              </Suspense>
            }
          />
          <Route
            path="reports/:reportPath"
            element={
              <Suspense fallback={<FullScreenLoader />}>
                <ReportRouter />
              </Suspense>
            }
          />
          <Route
            path="members"
            element={
              <Suspense fallback={<FullScreenLoader />}>
                <Members />
              </Suspense>
            }
          />
        </Route>
        <Route
          path="/tenant/*"
          element={
            <Suspense fallback={<FullScreenLoader />}>
              <Layout />
            </Suspense>
          }
        >
          <Route
            index
            element={
              <Suspense fallback={<FullScreenLoader />}>
                <TenantInfoRedirect />
              </Suspense>
            }
          />
          <Route
            path="details/:tenantId"
            element={
              <Suspense fallback={<FullScreenLoader />}>
                <TenantDetails />
              </Suspense>
            }
          />
          <Route
            path="details/:tenantId/settings"
            element={
              <Suspense fallback={<FullScreenLoader />}>
                <TenantConfig />
              </Suspense>
            }
          />
          <Route
            path="import"
            element={
              <Suspense fallback={<FullScreenLoader />}>
                <ImportTenant />
              </Suspense>
            }
          />
        </Route>
        <Route
          path="/actions/*"
          element={
            <Suspense fallback={<FullScreenLoader />}>
              <Layout />
            </Suspense>
          }
        >
          <Route
            path="manage"
            element={
              <Suspense fallback={<FullScreenLoader />}>
                <ManageActions />
              </Suspense>
            }
          />
        </Route>
        <Route
          path="/content/*"
          element={
            <Suspense fallback={<FullScreenLoader />}>
              <Layout />
            </Suspense>
          }
        >
          <Route index element={<Navigate to="faqs" replace />} />
          <Route
            path="faqs"
            element={
              <Suspense fallback={<FullScreenLoader />}>
                <FAQs />
              </Suspense>
            }
          />
          <Route
            path="cards"
            element={
              <Suspense fallback={<FullScreenLoader />}>
                <Cards />
              </Suspense>
            }
          />
          <Route
            path="agreements"
            element={
              <Suspense fallback={<FullScreenLoader />}>
                <Agreements />
              </Suspense>
            }
          />
        </Route>
      </Route>
    </Routes>
  );
}
